import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import { noop } from 'lodash';
import { getUserTicker } from '@paradigm/store/auth';

import { useApi } from '@paradigm/api';

import { useHedgeNettingFeatureFlag } from '#/feature-flags/context';
import { useRfqContext } from '#/unified-rfqs/ui/rfq-context';
import {
  useGrfqRfqQuotes,
  useGrfqService,
} from '#/unified-rfqs/contexts/grfqs';
import { CancelRfqQuotesBatchReq } from '#/unified-rfqs/repositories/grfq-types';
import { RfqId } from '#/unified-rfqs/entities/rfq';

import { useDrfqBboService } from '#/unified-rfqs/contexts/drfqs';
import { GrfqBbo } from '#/unified-rfqs/entities/grfq/bbo';
import { Bbo as DrfqBbo } from '#/unified-rfqs/entities/drfq/bbo';
import { createReadonlyObservable } from '@paradigm/utils/src/createObservable';
import { useObservable } from '@paradigm/utils/src/hooks';
import { useAcquireGrfqBbo } from './bbo';
import * as Rfq from './rfqs-functions';
import * as Quotes from './quotes-functions';

export function useRfqQuotesLoader(rfqId: RfqId) {
  const { listRfqQuotes } = useGrfqService();
  const [{ isLoading, resp: quotesResp }, fetchQuotes] = useApi(listRfqQuotes);

  const error = quotesResp?.ok === false ? quotesResp.message : '';

  useEffect(() => {
    fetchQuotes({ rfqId });
  }, [rfqId, fetchQuotes]);

  return { isLoading, error } as const;
}

export function useRfqRowQuotes(rfqId: RfqId) {
  const quotes = useGrfqRfqQuotes(rfqId);
  const myTicker = useSelector(getUserTicker);
  // Don't show closed quotes
  const asks = quotes.asks.filter(Quotes.isOpen);
  const bids = quotes.bids.filter(Quotes.isOpen);

  const topBidsAgg = Quotes.aggregateAdjacentByPrice(bids, 3);
  const topAsksAgg = Quotes.aggregateAdjacentByPrice(asks, 3);

  if (myTicker == null)
    return { topBidsAgg, topAsksAgg, myBidsAgg: [], myAsksAgg: [] };

  const myBids = bids.filter((q) => Quotes.isMine(q, myTicker));
  const myAsks = asks.filter((q) => Quotes.isMine(q, myTicker));

  const myBidsAgg = Quotes.aggregateAdjacentByPrice(myBids);
  const myAsksAgg = Quotes.aggregateAdjacentByPrice(myAsks);

  return { topBidsAgg, topAsksAgg, myBidsAgg, myAsksAgg };
}

export function useRfqQuotesCanceler(cancelQuery: CancelRfqQuotesBatchReq) {
  const { cancelRfqQuotesBatch } = useGrfqService();
  const [state, cancel] = useApi(cancelRfqQuotesBatch);

  return [
    state,
    (onError: (msg: string) => void) =>
      cancel(cancelQuery, (resp) => {
        if (!resp.ok) onError(resp.message);
      }),
  ] as const;
}

/**
 * Whether to warn that the mark price ignores the hedge leg.
 *
 * Only while hedge netting is off: with the flag on the mark does
 * account for the hedge, so there is nothing to warn about.
 */
export function useShouldWarnHedgeMark(): boolean {
  const { rfq } = useRfqContext();
  const bbo = useUnifiedBbo();
  const hedgeNettingEnabled = useHedgeNettingFeatureFlag();
  return (
    !hedgeNettingEnabled &&
    bbo.data != null &&
    rfq?.type === 'grfq' &&
    Rfq.isHedged(rfq)
  );
}

export function useFormattedMark(markPrice: string | null): string {
  const { rfq } = useRfqContext();
  const productCode = rfq?.product_codes[0];

  return useMemo(
    () => Rfq.formatProductMark(markPrice, productCode),
    [markPrice, productCode],
  );
}

const [EMPTY_BBO] = createReadonlyObservable<GrfqBbo | DrfqBbo | null>(
  'empty-unified-bbo',
  null,
);

export const useUnifiedBbo = () => {
  const { rfq } = useRfqContext();
  const drfqBboService = useDrfqBboService();
  const acquireGrfqBbo = useAcquireGrfqBbo();
  const [observable, setObservable] = useState(EMPTY_BBO);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setIsLoading(true);
    if (rfq == null) return;
    let release = noop;
    if (rfq.type === 'grfq') {
      acquireGrfqBbo(rfq.id)
        .then((bboResult) => {
          setObservable(bboResult.bbo);
          release = bboResult.release;
          setError(bboResult.error);
        })
        .finally(() => setIsLoading(false));
    } else {
      const [drfqRelease, drfqObservable] = drfqBboService.acquireRfqBbo(
        rfq.id,
      );
      release = drfqRelease;
      setObservable(drfqObservable);
      setIsLoading(false);
    }

    return release;
  }, [acquireGrfqBbo, drfqBboService, rfq]);

  const data = useObservable(observable);

  return {
    data,
    isLoading,
    error,
  };
};
